import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Create New View Dialog component
 * Allows users to create a new tab/view with a custom name
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when dialog open state changes
 * @param {Function} props.onCreate - Callback when a new view is created, receives the view name
 * @returns {JSX.Element} Create New View Dialog
 */
export default function CreateViewDialog({ open, onOpenChange, onCreate }) {
  const [viewName, setViewName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!viewName.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      await onCreate(viewName.trim());
      // Only close and clear if successful
      setViewName("");
      onOpenChange(false);
    } catch (err) {
      setError(err.message || "Failed to create view. Please try again.");
      // Don't close the dialog on error
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setViewName("");
    setError(null);
    onOpenChange(false);
  };

  // Clear error when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setViewName("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='text-lg font-semibold text-foreground'>
            Create New View
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <label
              htmlFor='view-name'
              className='text-sm font-medium text-foreground'>
              View Name
            </label>
            <Input
              id='view-name'
              type='text'
              placeholder='e.g., Pressure Comparison'
              value={viewName}
              onChange={(e) => {
                setViewName(e.target.value);
                setError(null); // Clear error when user types
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && viewName.trim() && !isCreating) {
                  handleCreate();
                }
              }}
              className='w-full'
              autoFocus
              disabled={isCreating}
            />
            {error && <p className='text-sm text-red-600 mt-2'>{error}</p>}
          </div>
        </div>
        <DialogFooter className='flex flex-row justify-end gap-2'>
          <Button
            onClick={handleCreate}
            disabled={!viewName.trim() || isCreating}
            className='font-light text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'>
            {isCreating ? "Creating..." : "Create"}
          </Button>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isCreating}
            className='font-light text-sm border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed'>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
